import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Box from 'apollo-react/components/Box';
import Grid from 'apollo-react/components/Grid';
import TextField from 'apollo-react/components/TextField';
import Select from 'apollo-react/components/Select';
import MenuItem from 'apollo-react/components/MenuItem';
import DatePicker from 'apollo-react/components/DatePicker';
import Button from 'apollo-react/components/Button';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Typography from 'apollo-react/components/Typography';
import { toast } from 'react-toastify';
import ProfileAttachmentBlock, {
  AttachmentSlotRevisionHistory,
} from '../components/ProfileAttachmentBlock';
import RepeatableSectionList from '../components/RepeatableSectionList';
import {
  ProfileWizardMultiFileField,
  ProfileWizardSingleFileField,
} from '../components/profileWizardFileFields';
import profileStyles from '../../customerProfile/profileUi/Profile.module.css';
import buttonStyles from '../../../components/common/Button.module.css';
import {
  accreditationStatusOptions,
  yesNoDropdownOptions,
  educationExperienceConfigDefaults,
  addressCountryOptions,
  hcpProfileFormFieldVariant,
  hcpProfileDatePickerTextFieldProps,
  hcpProfileTextFieldProps,
  normalizeAddressCountryValue,
  mergeOptionIfMissing,
  labelForAddressCountry,
} from '../../../components/HcpSignup/hcpSignupDefaults';
import {
  HCP_SIGNUP_FIELD_HINTS,
  HcpSignupLabelWithHint,
} from '../../../components/HcpSignup/HcpSignupFieldLabel';
import {
  buildEducationItemSchema,
  buildProfessionalExperienceSchema,
} from '../../../components/HcpSignup/educationExperienceSchema';
import {
  postHcpProfileEducation,
  putHcpProfileEducation,
  deleteHcpProfileEducation,
  putHcpProfileExperience,
} from '../hcpProfileApi';
import {
  datePickerValueToIsoDate,
  isoDateToDatePickerValue,
} from '../apolloDateUtils';
import {
  yupValidationErrorToFieldMap,
  pickStringApiFieldErrors,
} from '../utils/yupValidationErrors';

/** @param {string} text @param {keyof typeof HCP_SIGNUP_FIELD_HINTS} hintKey */
function eduExpFieldLabel(text, hintKey) {
  return (
    <HcpSignupLabelWithHint
      text={text}
      hint={HCP_SIGNUP_FIELD_HINTS[hintKey]}
    />
  );
}

const YES_NO_VALUE_SET = new Set(['yes', 'no']);

/** Map API / DB values to yes/no for the additional-experience control (signup parity). */
function experienceFieldToYesNo(raw) {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (YES_NO_VALUE_SET.has(s)) return s;
  if (!String(raw ?? '').trim()) return '';
  return 'yes';
}

function mapAdditionalExperienceFromApi(raw) {
  if (
    !educationExperienceConfigDefaults.experience
      .additionalExperienceSectionEnabled
  ) {
    return raw == null ? '' : String(raw);
  }
  return experienceFieldToYesNo(raw);
}

function formatAdditionalExperienceDisplay(value) {
  if (
    !educationExperienceConfigDefaults.experience
      .additionalExperienceSectionEnabled
  ) {
    return value || '—';
  }
  const yn = experienceFieldToYesNo(value);
  return yesNoDropdownOptions.find((o) => o.value === yn)?.label || yn || '—';
}

function yesNoLabel(value) {
  const v = String(value || '').toLowerCase();
  return yesNoDropdownOptions.find((o) => o.value === v)?.label || value || '—';
}

function normalizeExperienceApiFieldErrors(errs) {
  const base = pickStringApiFieldErrors(errs);
  const o = { ...base };
  if (o.internshipCertificate && !o.internshipCertificateFile) {
    o.internshipCertificateFile = o.internshipCertificate;
  }
  if (
    o.additionalExperienceCertificate &&
    !o.additionalExperienceCertificateFile
  ) {
    o.additionalExperienceCertificateFile = o.additionalExperienceCertificate;
  }
  if (
    o.undergraduateInternshipCertificate &&
    !o.undergraduateInternshipCertificateFile
  ) {
    o.undergraduateInternshipCertificateFile =
      o.undergraduateInternshipCertificate;
  }
  return o;
}

function mapEduFromApi(api) {
  return {
    educationRowId: api.educationRowId,
    countryOfCertificate: normalizeAddressCountryValue(
      api.countryOfCertificate || ''
    ),
    degreeName: api.degreeName || '',
    institutionName: api.institutionName || '',
    authorityName: api.authorityName || '',
    fieldOfStudy: api.fieldOfStudy || '',
    graduationDate: api.graduationDate
      ? String(api.graduationDate).slice(0, 10)
      : '',
    studyYears: api.studyYears ?? 6,
    accreditationStatus: api.accreditationStatus || 'not_accredited',
    accreditingAuthority: api.accreditingAuthority || '',
    existingAccreditationDocumentUploadId:
      api.accreditationDocument?.id ?? null,
    accreditationDocument: api.accreditationDocument,
    accreditationFile: null,
  };
}

function emptyEduDraft() {
  return {
    countryOfCertificate: '',
    degreeName: '',
    institutionName: '',
    authorityName: '',
    fieldOfStudy: '',
    graduationDate: '',
    studyYears: 6,
    accreditationStatus: 'not_accredited',
    accreditingAuthority: '',
    existingAccreditationDocumentUploadId: null,
    accreditationFile: null,
  };
}

function eduRowToDraft(r) {
  return {
    countryOfCertificate: r.countryOfCertificate || '',
    degreeName: r.degreeName || '',
    institutionName: r.institutionName || '',
    authorityName: r.authorityName || '',
    fieldOfStudy: r.fieldOfStudy || '',
    graduationDate: r.graduationDate || '',
    studyYears: r.studyYears ?? 6,
    accreditationStatus: r.accreditationStatus || 'not_accredited',
    accreditingAuthority: r.accreditingAuthority || '',
    existingAccreditationDocumentUploadId:
      r.existingAccreditationDocumentUploadId ??
      r.accreditationDocument?.id ??
      null,
    accreditationFile: null,
  };
}

function experienceFromApi(ex) {
  if (!ex) {
    return {
      internshipTraining: 'no',
      postGraduationInternship: '',
      internshipCompletion: '',
      internshipStart: '',
      internshipEnd: '',
      additionalExperience: '',
      additionalExperienceStart: '',
      additionalExperienceEnd: '',
      undergraduateInternship: '',
      undergraduateInternshipStart: '',
      undergraduateInternshipEnd: '',
      internshipCertificateFile: null,
      additionalExperienceCertificateFile: null,
      undergraduateInternshipCertificateFile: null,
      relevantAttachmentFiles: [],
      existingInternshipCertificateUploadId: null,
      existingAdditionalExperienceCertificateUploadId: null,
      existingUndergraduateInternshipCertificateUploadId: null,
      existingRelevantAttachmentIds: [],
      internshipCertificateObj: null,
      additionalExperienceCertificateObj: null,
      undergraduateInternshipCertificateObj: null,
      relevantAttachments: [],
    };
  }
  return {
    internshipTraining: ex.internshipTraining || 'no',
    postGraduationInternship: experienceFieldToYesNo(
      ex.postGraduationInternship
    ),
    internshipCompletion: experienceFieldToYesNo(ex.internshipCompletion),
    internshipStart: ex.internshipStart
      ? String(ex.internshipStart).slice(0, 10)
      : '',
    internshipEnd: ex.internshipEnd
      ? String(ex.internshipEnd).slice(0, 10)
      : '',
    additionalExperience: mapAdditionalExperienceFromApi(
      ex.additionalExperience
    ),
    additionalExperienceStart: ex.additionalExperienceStart
      ? String(ex.additionalExperienceStart).slice(0, 10)
      : '',
    additionalExperienceEnd: ex.additionalExperienceEnd
      ? String(ex.additionalExperienceEnd).slice(0, 10)
      : '',
    undergraduateInternship: ex.undergraduateInternship || '',
    undergraduateInternshipStart: ex.undergraduateInternshipStart
      ? String(ex.undergraduateInternshipStart).slice(0, 10)
      : '',
    undergraduateInternshipEnd: ex.undergraduateInternshipEnd
      ? String(ex.undergraduateInternshipEnd).slice(0, 10)
      : '',
    internshipCertificateFile: null,
    additionalExperienceCertificateFile: null,
    undergraduateInternshipCertificateFile: null,
    relevantAttachmentFiles: [],
    existingInternshipCertificateUploadId: ex.internshipCertificate?.id ?? null,
    existingAdditionalExperienceCertificateUploadId:
      ex.additionalExperienceCertificate?.id ?? null,
    existingUndergraduateInternshipCertificateUploadId:
      ex.undergraduateInternshipCertificate?.id ?? null,
    existingRelevantAttachmentIds: Array.isArray(ex.relevantAttachmentIds)
      ? [...ex.relevantAttachmentIds]
      : [],
    internshipCertificateObj: ex.internshipCertificate ?? null,
    additionalExperienceCertificateObj:
      ex.additionalExperienceCertificate ?? null,
    undergraduateInternshipCertificateObj:
      ex.undergraduateInternshipCertificate ?? null,
    relevantAttachments: Array.isArray(ex.relevantAttachments)
      ? ex.relevantAttachments
      : [],
  };
}

/** @returns {Record<string, unknown>} */
function experiencePayloadForValidate(d) {
  return {
    internshipTraining: d.internshipTraining,
    postGraduationInternship: d.postGraduationInternship,
    internshipCompletion: d.internshipCompletion,
    internshipStart: d.internshipStart,
    internshipEnd: d.internshipEnd,
    internshipCertificateFile: d.internshipCertificateFile,
    existingInternshipCertificateUploadId:
      d.existingInternshipCertificateUploadId,
    additionalExperience: d.additionalExperience,
    additionalExperienceStart: d.additionalExperienceStart,
    additionalExperienceEnd: d.additionalExperienceEnd,
    additionalExperienceCertificateFile: d.additionalExperienceCertificateFile,
    existingAdditionalExperienceCertificateUploadId:
      d.existingAdditionalExperienceCertificateUploadId,
    undergraduateInternship: d.undergraduateInternship,
    undergraduateInternshipStart: d.undergraduateInternshipStart,
    undergraduateInternshipEnd: d.undergraduateInternshipEnd,
    undergraduateInternshipCertificateFile:
      d.undergraduateInternshipCertificateFile,
    existingUndergraduateInternshipCertificateUploadId:
      d.existingUndergraduateInternshipCertificateUploadId,
    relevantAttachmentFiles: d.relevantAttachmentFiles || [],
    existingRelevantAttachmentIds: d.existingRelevantAttachmentIds || [],
  };
}

export default function EducationExperienceTab({
  educations,
  experience,
  educationRepeatableLimits,
  attachmentRevisions = [],
  onSaved,
  showPageTitle = true,
  /** When true, no add/edit/delete (e.g. attached profile snapshot). */
  readOnly = false,
  /** When false, hide superseded / prior-version attachment UI for this tab. */
  showAttachmentPriorVersions = true,
  /** Admin request review: hide Add / Edit / Delete and experience edit chrome. */
  suppressApplicantActionChrome = false,
}) {
  const eduItemSchema = useMemo(() => buildEducationItemSchema(), []);
  const expSchema = useMemo(() => buildProfessionalExperienceSchema(), []);

  const eduRows = useMemo(
    () => (Array.isArray(educations) ? educations.map(mapEduFromApi) : []),
    [educations]
  );

  const eduMinEntries = educationRepeatableLimits?.minEntries ?? 0;
  const eduMaxEntries =
    educationRepeatableLimits?.maxEntries == null
      ? null
      : educationRepeatableLimits.maxEntries;

  const [eduSectionBusy, setEduSectionBusy] = useState(false);

  const [expData, setExpData] = useState(() => experienceFromApi(null));
  useEffect(() => {
    setExpData(experienceFromApi(experience || null));
  }, [experience]);

  const [expEditing, setExpEditing] = useState(false);
  const [expDraft, setExpDraft] = useState(null);
  const [expBusy, setExpBusy] = useState(false);
  const [expFieldErrors, setExpFieldErrors] = useState({});

  useEffect(() => {
    if (readOnly) {
      setExpEditing(false);
      setExpDraft(null);
      setExpFieldErrors({});
    }
  }, [readOnly]);

  const updateExpDraft = useCallback((k, v) => {
    setExpFieldErrors((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, k)) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
    setExpDraft((p) => (p ? { ...p, [k]: v } : p));
  }, []);

  const beginEditExp = () => {
    setExpFieldErrors({});
    setExpDraft(JSON.parse(JSON.stringify(expData)));
    setExpEditing(true);
  };

  const cancelExp = () => {
    setExpDraft(null);
    setExpEditing(false);
    setExpFieldErrors({});
  };

  const saveExp = async () => {
    if (!expDraft) return;
    try {
      await expSchema.validate(experiencePayloadForValidate(expDraft), {
        abortEarly: false,
      });
    } catch (yupErr) {
      if (yupErr?.name === 'ValidationError') {
        setExpFieldErrors(yupValidationErrorToFieldMap(yupErr));
        const first =
          (yupErr.inner && yupErr.inner[0] && yupErr.inner[0].message) ||
          yupErr.message ||
          'Validation failed';
        toast.error(first);
        return;
      }
      throw yupErr;
    }

    const payload = {
      internshipTraining: expDraft.internshipTraining,
      postGraduationInternship: expDraft.postGraduationInternship,
      internshipCompletion: expDraft.internshipCompletion,
      internshipStart: expDraft.internshipStart,
      internshipEnd: expDraft.internshipEnd,
      existingInternshipCertificateUploadId:
        expDraft.existingInternshipCertificateUploadId,
      additionalExperience: expDraft.additionalExperience,
      additionalExperienceStart: expDraft.additionalExperienceStart,
      additionalExperienceEnd: expDraft.additionalExperienceEnd,
      existingAdditionalExperienceCertificateUploadId:
        expDraft.existingAdditionalExperienceCertificateUploadId,
      undergraduateInternship: expDraft.undergraduateInternship,
      undergraduateInternshipStart: expDraft.undergraduateInternshipStart,
      undergraduateInternshipEnd: expDraft.undergraduateInternshipEnd,
      existingUndergraduateInternshipCertificateUploadId:
        expDraft.existingUndergraduateInternshipCertificateUploadId,
      existingRelevantAttachmentIds:
        expDraft.existingRelevantAttachmentIds || [],
    };

    setExpBusy(true);
    try {
      await putHcpProfileExperience(
        payload,
        expDraft.internshipCertificateFile,
        expDraft.relevantAttachmentFiles || [],
        expDraft.additionalExperienceCertificateFile,
        expDraft.undergraduateInternshipCertificateFile
      );
      toast.success('Professional experience updated');
      cancelExp();
      await onSaved?.();
    } catch (e) {
      const apiMap = normalizeExperienceApiFieldErrors(
        e.response?.data?.errors
      );
      if (Object.keys(apiMap).length) {
        setExpFieldErrors((prev) => ({ ...prev, ...apiMap }));
      }
      const errs = e.response?.data?.errors;
      const msg =
        (errs &&
          typeof errs === 'object' &&
          Object.values(errs).find((x) => typeof x === 'string')) ||
        e.response?.data?.message ||
        'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    } finally {
      setExpBusy(false);
    }
  };

  const trainingYes = expData.internshipTraining === 'yes';
  const expDraftTrainingYes = expDraft?.internshipTraining === 'yes';
  const expDraftAdditionalYes = expDraft?.additionalExperience === 'yes';

  const accreditationLabel = (value) =>
    accreditationStatusOptions.find((o) => o.value === value)?.label ||
    value ||
    '—';

  return (
    <div className="w-100">
      {showPageTitle ? (
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Education and Experience
        </Typography>
      ) : null}

      <section className="mb-4">
        <RepeatableSectionList
          items={eduRows}
          getRowId={(r) => r.educationRowId}
          minEntries={eduMinEntries}
          maxEntries={eduMaxEntries}
          locked={readOnly || expEditing}
          showAddButton={!suppressApplicantActionChrome}
          crossLockNotice={
            readOnly ? null : (
              <span>
                Finish or cancel professional experience edits before adding or
                editing education entries.
              </span>
            )
          }
          onLockStateChange={setEduSectionBusy}
          title="Education"
          titleVariant="subsection"
          addButtonLabel="Add education entry"
          emptyMessage="No education entries."
          editorTitleNew="New education entry"
          editorTitleEdit="Edit education entry"
          deleteModalTitle="Remove education entry"
          finishEditingHint="Finish or cancel the education row you are editing before starting another change."
          createEmptyDraft={emptyEduDraft}
          rowToDraft={eduRowToDraft}
          validateDraft={(draft) =>
            eduItemSchema.validate(
              { _rowKey: 'e', ...draft },
              { abortEarly: false }
            )
          }
          saveDraft={async (draft, { isNew, editingId }) => {
            const fields = {
              countryOfCertificate: draft.countryOfCertificate,
              degreeName: draft.degreeName,
              institutionName: draft.institutionName,
              authorityName: draft.authorityName,
              fieldOfStudy: draft.fieldOfStudy,
              graduationDate: draft.graduationDate,
              studyYears: draft.studyYears,
              accreditationStatus: draft.accreditationStatus,
              accreditingAuthority: draft.accreditingAuthority,
              existingAccreditationDocumentUploadId:
                draft.existingAccreditationDocumentUploadId,
            };
            if (isNew) {
              await postHcpProfileEducation(fields, draft.accreditationFile);
              toast.success('Education entry added');
            } else {
              await putHcpProfileEducation(
                editingId,
                fields,
                draft.accreditationFile
              );
              toast.success('Education entry updated');
            }
          }}
          deleteItem={async (rowId) => {
            await deleteHcpProfileEducation(rowId);
            toast.success('Education entry removed');
          }}
          getDeleteConfirmLabel={(r) =>
            r.degreeName || String(r.educationRowId)
          }
          onSaved={onSaved}
          renderEditor={({
            draft: d,
            updateDraft,
            isNew,
            editingId,
            rows: editorRows,
            saving,
            fieldErrors: fe = {},
          }) => {
            const acc = d.accreditationStatus === 'accredited';
            const countryOfCertOptions = mergeOptionIfMissing(
              addressCountryOptions,
              d.countryOfCertificate
            );
            const defaultAccreditingHint =
              'Required when status is Accredited.';
            const defaultAccFileHint = acc
              ? isNew
                ? 'PDF, JPG, or PNG — upload required when Accredited unless reusing via server rules.'
                : 'Upload to replace when Accredited.'
              : 'Upload only if accredited or leave blank otherwise.';
            return (
              <Grid
                container
                rowSpacing={0}
                columnSpacing={2}
                className={`mb-3 ${profileStyles.wizardFormGrid}`}
              >
                <Grid item xs={12} md={6}>
                  <Select
                    label="Country of certificate"
                    fullWidth
                    variant={hcpProfileFormFieldVariant}
                    disabled={saving}
                    value={d.countryOfCertificate}
                    onChange={(e) =>
                      updateDraft('countryOfCertificate', e.target.value)
                    }
                    error={Boolean(fe.countryOfCertificate)}
                    helperText={fe.countryOfCertificate || ''}
                  >
                    <MenuItem value="" disabled>
                      <em>Select country</em>
                    </MenuItem>
                    {countryOfCertOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...hcpProfileTextFieldProps}
                    label={eduExpFieldLabel('Degree name', 'eduDegreeName')}
                    disabled={saving}
                    value={d.degreeName}
                    onChange={(e) => updateDraft('degreeName', e.target.value)}
                    error={Boolean(fe.degreeName)}
                    helperText={fe.degreeName || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    {...hcpProfileTextFieldProps}
                    label={eduExpFieldLabel(
                      'Institution name',
                      'eduInstitutionName'
                    )}
                    disabled={saving}
                    value={d.institutionName}
                    onChange={(e) =>
                      updateDraft('institutionName', e.target.value)
                    }
                    error={Boolean(fe.institutionName)}
                    helperText={fe.institutionName || ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...hcpProfileTextFieldProps}
                    label={eduExpFieldLabel(
                      'Authority name',
                      'eduAuthorityName'
                    )}
                    disabled={saving}
                    value={d.authorityName}
                    onChange={(e) =>
                      updateDraft('authorityName', e.target.value)
                    }
                    error={Boolean(fe.authorityName)}
                    helperText={fe.authorityName || ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...hcpProfileTextFieldProps}
                    label={eduExpFieldLabel(
                      'Field of study',
                      'eduFieldOfStudy'
                    )}
                    disabled={saving}
                    value={d.fieldOfStudy}
                    onChange={(e) =>
                      updateDraft('fieldOfStudy', e.target.value)
                    }
                    error={Boolean(fe.fieldOfStudy)}
                    helperText={fe.fieldOfStudy || ''}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label={eduExpFieldLabel(
                      'Graduation date',
                      'eduGraduationDate'
                    )}
                    fullWidth
                    disabled={saving}
                    value={isoDateToDatePickerValue(d.graduationDate)}
                    onChange={(v) =>
                      updateDraft('graduationDate', datePickerValueToIsoDate(v))
                    }
                    helperText={
                      fe.graduationDate || 'Must be on or before today.'
                    }
                    TextFieldProps={{
                      ...hcpProfileDatePickerTextFieldProps,
                      error: Boolean(fe.graduationDate),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    {...hcpProfileTextFieldProps}
                    label={eduExpFieldLabel(
                      'Study years (1–10)',
                      'eduStudyYears'
                    )}
                    type="number"
                    disabled={saving}
                    inputProps={{ min: 1, max: 10 }}
                    value={d.studyYears}
                    onChange={(e) =>
                      updateDraft('studyYears', Number(e.target.value))
                    }
                    error={Boolean(fe.studyYears)}
                    helperText={fe.studyYears || ''}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Select
                    label="Accreditation status"
                    fullWidth
                    variant={hcpProfileFormFieldVariant}
                    disabled={saving}
                    value={d.accreditationStatus}
                    onChange={(e) =>
                      updateDraft('accreditationStatus', e.target.value)
                    }
                    error={Boolean(fe.accreditationStatus)}
                    helperText={fe.accreditationStatus || ''}
                  >
                    {accreditationStatusOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                {acc ? (
                  <Grid item xs={12}>
                    <TextField
                      {...hcpProfileTextFieldProps}
                      label={eduExpFieldLabel(
                        'Accrediting authority',
                        'eduAccreditingAuthority'
                      )}
                      disabled={saving}
                      value={d.accreditingAuthority}
                      onChange={(e) =>
                        updateDraft('accreditingAuthority', e.target.value)
                      }
                      error={Boolean(fe.accreditingAuthority)}
                      helperText={
                        fe.accreditingAuthority || defaultAccreditingHint
                      }
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12}>
                  <div className={profileStyles.attachmentPanel}>
                    <ProfileWizardSingleFileField
                      id={`hcp_profile_edu_accreditation_${editingId ?? 'new'}`}
                      label="Accreditation document"
                      disabled={saving}
                      localFile={d.accreditationFile}
                      existingServerFile={
                        !isNew && d.existingAccreditationDocumentUploadId
                          ? editorRows.find(
                              (x) => x.educationRowId === editingId
                            )?.accreditationDocument || {
                              id: d.existingAccreditationDocumentUploadId,
                              originalName: 'Current document',
                            }
                          : null
                      }
                      onFileChange={(f) => {
                        updateDraft('accreditationFile', f);
                        if (f)
                          updateDraft(
                            'existingAccreditationDocumentUploadId',
                            null
                          );
                      }}
                      onClearExistingServerFile={() =>
                        updateDraft(
                          'existingAccreditationDocumentUploadId',
                          null
                        )
                      }
                      preset="attachment"
                      requiredHint={acc}
                      showError={Boolean(fe.accreditationFile)}
                      helperText={fe.accreditationFile || defaultAccFileHint}
                    />
                    {typeof editingId === 'number' &&
                    showAttachmentPriorVersions ? (
                      <AttachmentSlotRevisionHistory
                        slotKey={`education:${editingId}`}
                        attachmentRevisions={attachmentRevisions}
                        title="Prior accreditation uploads"
                      />
                    ) : null}
                  </div>
                </Grid>
              </Grid>
            );
          }}
          renderRow={({
            item: r,
            index: idx,
            canDelete,
            globalLock,
            deleteLocked,
            onEdit,
            onRequestDelete,
          }) => (
            <div className={profileStyles.nestedCard}>
              <div className={profileStyles.recordBadge}>
                Education record #{idx + 1}
              </div>
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>
                  Degree — institution:
                </span>
                <span className={profileStyles.value}>
                  {`${r.degreeName || '—'} — ${r.institutionName || '—'}`}
                </span>
              </div>
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>
                  Country of certificate:
                </span>
                <span className={profileStyles.value}>
                  {labelForAddressCountry(r.countryOfCertificate)}
                </span>
              </div>
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>Authority:</span>
                <span className={profileStyles.value}>
                  {r.authorityName || '—'}
                </span>
              </div>
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>Field of study:</span>
                <span className={profileStyles.value}>
                  {r.fieldOfStudy || '—'}
                </span>
              </div>
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>Graduation:</span>
                <span className={profileStyles.value}>
                  {r.graduationDate || '—'}
                </span>
              </div>
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>Years of study:</span>
                <span className={profileStyles.value}>
                  {r.studyYears != null ? String(r.studyYears) : '—'}
                </span>
              </div>
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>Accreditation:</span>
                <span className={profileStyles.value}>
                  {accreditationLabel(r.accreditationStatus)}
                </span>
              </div>
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>
                  Accrediting authority:
                </span>
                <span className={profileStyles.value}>
                  {r.accreditingAuthority || '—'}
                </span>
              </div>
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>
                  Accreditation document:
                </span>
                <div className={profileStyles.valueColumn}>
                  <div className={profileStyles.attachmentPanel}>
                    <ProfileAttachmentBlock
                      file={r.accreditationDocument}
                      slotKey={`education:${r.educationRowId}`}
                      attachmentRevisions={attachmentRevisions}
                      showPriorVersions={showAttachmentPriorVersions}
                    />
                  </div>
                </div>
              </div>
              {!suppressApplicantActionChrome ? (
                <div className={profileStyles.inlineActions}>
                  <Button
                    variant="secondary"
                    size="small"
                    className={buttonStyles.button}
                    disabled={globalLock}
                    onClick={onEdit}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    className={buttonStyles.button}
                    disabled={globalLock || deleteLocked || !canDelete}
                    onClick={onRequestDelete}
                  >
                    Delete
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        />
      </section>

      <section>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap="wrap"
          gap={2}
        >
          <div className={profileStyles.header}>Professional experience</div>
          {!expEditing && !readOnly && !suppressApplicantActionChrome ? (
            <Button
              variant="secondary"
              size="small"
              className={buttonStyles.button}
              disabled={eduSectionBusy}
              onClick={beginEditExp}
            >
              Edit experience
            </Button>
          ) : null}
        </Box>

        {!expEditing ? (
          <div className={profileStyles.nestedCard}>
            <div className={profileStyles.row}>
              <span className={profileStyles.label}>
                Internship / training completed:
              </span>
              <span className={profileStyles.value}>
                {yesNoDropdownOptions.find(
                  (o) => o.value === expData.internshipTraining
                )?.label ||
                  expData.internshipTraining ||
                  '—'}
              </span>
            </div>
            {trainingYes ? (
              <>
                <div className={profileStyles.row}>
                  <span className={profileStyles.label}>
                    Post-graduation internship:
                  </span>
                  <span className={profileStyles.value}>
                    {yesNoLabel(expData.postGraduationInternship)}
                  </span>
                </div>
                <div className={profileStyles.row}>
                  <span className={profileStyles.label}>
                    Internship completed:
                  </span>
                  <span className={profileStyles.value}>
                    {yesNoLabel(expData.internshipCompletion)}
                  </span>
                </div>
                <div className={profileStyles.row}>
                  <span className={profileStyles.label}>
                    Internship period:
                  </span>
                  <span className={profileStyles.value}>
                    {`${expData.internshipStart || '—'} — ${expData.internshipEnd || '—'}`}
                  </span>
                </div>
                <div className={profileStyles.row}>
                  <span className={profileStyles.label}>
                    Internship completion certificate:
                  </span>
                  <div className={profileStyles.valueColumn}>
                    <div className={profileStyles.attachmentPanel}>
                      <ProfileAttachmentBlock
                        file={expData.internshipCertificateObj}
                        slotKey="experience:internship_certificate"
                        attachmentRevisions={attachmentRevisions}
                        showPriorVersions={showAttachmentPriorVersions}
                      />
                    </div>
                  </div>
                </div>
                {educationExperienceConfigDefaults.experience
                  .additionalExperienceSectionEnabled ? (
                  <div className={profileStyles.row}>
                    <span className={profileStyles.label}>
                      Additional experience:
                    </span>
                    <span className={profileStyles.value}>
                      {formatAdditionalExperienceDisplay(
                        expData.additionalExperience
                      )}
                    </span>
                  </div>
                ) : expData.additionalExperience ? (
                  <div className={profileStyles.row}>
                    <span className={profileStyles.label}>
                      Additional experience:
                    </span>
                    <span
                      className={profileStyles.value}
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {expData.additionalExperience}
                    </span>
                  </div>
                ) : null}
                {educationExperienceConfigDefaults.experience
                  .additionalExperienceSectionEnabled &&
                String(expData.additionalExperience || '').toLowerCase() ===
                  'yes' ? (
                  <>
                    <div className={profileStyles.row}>
                      <span className={profileStyles.label}>
                        Additional experience period:
                      </span>
                      <span className={profileStyles.value}>
                        {`${expData.additionalExperienceStart || '—'} — ${
                          expData.additionalExperienceEnd || '—'
                        }`}
                      </span>
                    </div>
                    <div className={profileStyles.row}>
                      <span className={profileStyles.label}>
                        Additional experience certificate:
                      </span>
                      <div className={profileStyles.valueColumn}>
                        <div className={profileStyles.attachmentPanel}>
                          <ProfileAttachmentBlock
                            file={expData.additionalExperienceCertificateObj}
                            slotKey="experience:additional_experience_certificate"
                            attachmentRevisions={attachmentRevisions}
                            showPriorVersions={showAttachmentPriorVersions}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
            {expData.undergraduateInternship ? (
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>
                  Undergraduate internship:
                </span>
                <span
                  className={profileStyles.value}
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {expData.undergraduateInternship}
                </span>
              </div>
            ) : null}
            {expData.undergraduateInternshipStart ||
            expData.undergraduateInternshipEnd ? (
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>
                  Undergraduate internship period:
                </span>
                <span className={profileStyles.value}>
                  {`${expData.undergraduateInternshipStart || '—'} — ${
                    expData.undergraduateInternshipEnd || '—'
                  }`}
                </span>
              </div>
            ) : null}
            {expData.undergraduateInternshipCertificateObj ? (
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>
                  Undergraduate internship certificate:
                </span>
                <div className={profileStyles.valueColumn}>
                  <div className={profileStyles.attachmentPanel}>
                    <ProfileAttachmentBlock
                      file={expData.undergraduateInternshipCertificateObj}
                      slotKey="experience:undergraduate_internship_certificate"
                      attachmentRevisions={attachmentRevisions}
                      showPriorVersions={showAttachmentPriorVersions}
                    />
                  </div>
                </div>
              </div>
            ) : null}
            {Array.isArray(expData.relevantAttachments) &&
            expData.relevantAttachments.length ? (
              <div className={profileStyles.row}>
                <span className={profileStyles.label}>
                  Supporting attachments:
                </span>
                <div className={profileStyles.valueColumn}>
                  <div className={profileStyles.attachmentPanel}>
                    {expData.relevantAttachments.map((f) => (
                      <Box key={f.id} sx={{ mr: 1, mb: 1 }} component="div">
                        <ProfileAttachmentBlock
                          compact
                          showPriorVersions={showAttachmentPriorVersions}
                          file={f}
                          attachmentRevisions={attachmentRevisions}
                        />
                      </Box>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
            {showAttachmentPriorVersions ? (
              <AttachmentSlotRevisionHistory
                slotKey="experience:relevant"
                attachmentRevisions={attachmentRevisions}
                title="Supporting attachments — prior uploads"
              />
            ) : null}
          </div>
        ) : (
          expDraft && (
            <div className={profileStyles.editorSurface}>
              <Box
                display="flex"
                alignItems="flex-start"
                mb={2}
                flexWrap="wrap"
                gap={2}
              >
                <div className={profileStyles.header}>
                  Edit professional experience
                </div>
              </Box>

              <Grid
                container
                rowSpacing={0}
                columnSpacing={2}
                className={profileStyles.wizardFormGrid}
              >
                <Grid item xs={12}>
                  <Select
                    label={
                      <HcpSignupLabelWithHint
                        text="Internship / training completed"
                        hint={HCP_SIGNUP_FIELD_HINTS.expInternshipTraining}
                        showRequiredAfterText={
                          educationExperienceConfigDefaults.experience
                            .internshipTrainingRequired
                        }
                      />
                    }
                    fullWidth
                    variant={hcpProfileFormFieldVariant}
                    disabled={expBusy}
                    value={expDraft.internshipTraining}
                    onChange={(e) =>
                      updateExpDraft('internshipTraining', e.target.value)
                    }
                    error={Boolean(expFieldErrors.internshipTraining)}
                    helperText={expFieldErrors.internshipTraining || ''}
                  >
                    {[
                      ...(educationExperienceConfigDefaults.experience
                        .internshipTrainingRequired
                        ? []
                        : [
                            <MenuItem
                              key="__internship_training_empty"
                              value=""
                              disabled
                            >
                              <em>Select…</em>
                            </MenuItem>,
                          ]),
                      ...yesNoDropdownOptions.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      )),
                    ]}
                  </Select>
                </Grid>

                {expDraftTrainingYes ? (
                  <>
                    <Grid item xs={12}>
                      <Select
                        label={
                          <HcpSignupLabelWithHint
                            text="Post-graduation internship"
                            hint={HCP_SIGNUP_FIELD_HINTS.expPostGradInternship}
                            showRequiredAfterText={
                              educationExperienceConfigDefaults.experience
                                .postGradInternRequired
                            }
                          />
                        }
                        fullWidth
                        variant={hcpProfileFormFieldVariant}
                        disabled={expBusy}
                        value={expDraft.postGraduationInternship || ''}
                        onChange={(e) =>
                          updateExpDraft(
                            'postGraduationInternship',
                            e.target.value
                          )
                        }
                        error={Boolean(expFieldErrors.postGraduationInternship)}
                        helperText={
                          expFieldErrors.postGraduationInternship || ''
                        }
                      >
                        <MenuItem value="" disabled>
                          <em>Select…</em>
                        </MenuItem>
                        {yesNoDropdownOptions.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={12}>
                      <Select
                        label={
                          <HcpSignupLabelWithHint
                            text="Internship completed"
                            hint={
                              HCP_SIGNUP_FIELD_HINTS.expInternshipCompletion
                            }
                            showRequiredAfterText={
                              educationExperienceConfigDefaults.experience
                                .internshipCompletionRequired
                            }
                          />
                        }
                        fullWidth
                        variant={hcpProfileFormFieldVariant}
                        disabled={expBusy}
                        value={expDraft.internshipCompletion || ''}
                        onChange={(e) =>
                          updateExpDraft('internshipCompletion', e.target.value)
                        }
                        error={Boolean(expFieldErrors.internshipCompletion)}
                        helperText={expFieldErrors.internshipCompletion || ''}
                      >
                        <MenuItem value="" disabled>
                          <em>Select…</em>
                        </MenuItem>
                        {yesNoDropdownOptions.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label={eduExpFieldLabel(
                          'Internship start',
                          'expInternshipStart'
                        )}
                        fullWidth
                        disabled={expBusy}
                        value={isoDateToDatePickerValue(
                          expDraft.internshipStart
                        )}
                        onChange={(v) =>
                          updateExpDraft(
                            'internshipStart',
                            datePickerValueToIsoDate(v)
                          )
                        }
                        TextFieldProps={{
                          ...hcpProfileDatePickerTextFieldProps,
                          error: Boolean(expFieldErrors.internshipStart),
                          helperText: expFieldErrors.internshipStart || '',
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label={eduExpFieldLabel(
                          'Internship end',
                          'expInternshipEnd'
                        )}
                        fullWidth
                        disabled={expBusy}
                        value={isoDateToDatePickerValue(expDraft.internshipEnd)}
                        onChange={(v) =>
                          updateExpDraft(
                            'internshipEnd',
                            datePickerValueToIsoDate(v)
                          )
                        }
                        TextFieldProps={{
                          ...hcpProfileDatePickerTextFieldProps,
                          error: Boolean(expFieldErrors.internshipEnd),
                          helperText: expFieldErrors.internshipEnd || '',
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <div className={profileStyles.attachmentPanel}>
                        <ProfileWizardSingleFileField
                          id="hcp_profile_exp_internship_cert"
                          label={eduExpFieldLabel(
                            'Internship completion certificate',
                            'expInternshipCertificate'
                          )}
                          disabled={expBusy}
                          localFile={expDraft.internshipCertificateFile}
                          existingServerFile={
                            expDraft.existingInternshipCertificateUploadId
                              ? expData.internshipCertificateObj
                              : null
                          }
                          onFileChange={(f) => {
                            updateExpDraft('internshipCertificateFile', f);
                            if (f)
                              updateExpDraft(
                                'existingInternshipCertificateUploadId',
                                null
                              );
                          }}
                          onClearExistingServerFile={() =>
                            updateExpDraft(
                              'existingInternshipCertificateUploadId',
                              null
                            )
                          }
                          preset="attachment"
                          requiredHint={
                            expDraft.internshipCompletion === 'yes' &&
                            educationExperienceConfigDefaults.experience
                              .internshipCertificateRequiredWhenInternshipCompletionYes
                          }
                          showError={Boolean(
                            expFieldErrors.internshipCertificateFile
                          )}
                          helperText={
                            expFieldErrors.internshipCertificateFile ||
                            (educationExperienceConfigDefaults.experience
                              .internshipCertificateRequiredWhenInternshipCompletionYes
                              ? 'Required when internship is completed (Yes), unless the current file is kept.'
                              : undefined)
                          }
                        />
                        {showAttachmentPriorVersions ? (
                          <AttachmentSlotRevisionHistory
                            slotKey="experience:internship_certificate"
                            attachmentRevisions={attachmentRevisions}
                            title="Prior certificate uploads"
                          />
                        ) : null}
                      </div>
                    </Grid>
                    {educationExperienceConfigDefaults.experience
                      .additionalExperienceSectionEnabled ? (
                      <Grid item xs={12}>
                        <Select
                          label={
                            <HcpSignupLabelWithHint
                              text="Additional experience"
                              hint={
                                HCP_SIGNUP_FIELD_HINTS.expAdditionalExperience
                              }
                              showRequiredAfterText={
                                educationExperienceConfigDefaults.experience
                                  .additionalExperienceRequiredWhenInternshipYes
                              }
                            />
                          }
                          fullWidth
                          variant={hcpProfileFormFieldVariant}
                          disabled={expBusy}
                          value={expDraft.additionalExperience || ''}
                          onChange={(e) =>
                            updateExpDraft(
                              'additionalExperience',
                              e.target.value
                            )
                          }
                          error={Boolean(expFieldErrors.additionalExperience)}
                          helperText={expFieldErrors.additionalExperience || ''}
                        >
                          <MenuItem value="" disabled>
                            <em>Select…</em>
                          </MenuItem>
                          {yesNoDropdownOptions.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    ) : expDraft.additionalExperience ? (
                      <Grid item xs={12}>
                        <TextField
                          {...hcpProfileTextFieldProps}
                          label={eduExpFieldLabel(
                            'Additional experience',
                            'expAdditionalExperience'
                          )}
                          disabled
                          multiline
                          minRows={2}
                          value={expDraft.additionalExperience}
                          helperText="This section is disabled in the current configuration; value is read-only."
                        />
                      </Grid>
                    ) : null}
                    {educationExperienceConfigDefaults.experience
                      .additionalExperienceSectionEnabled &&
                    expDraftAdditionalYes ? (
                      <>
                        <Grid item xs={12} md={6}>
                          <DatePicker
                            label={eduExpFieldLabel(
                              'Additional experience start',
                              'expInternshipStart'
                            )}
                            fullWidth
                            disabled={expBusy}
                            value={isoDateToDatePickerValue(
                              expDraft.additionalExperienceStart
                            )}
                            onChange={(v) =>
                              updateExpDraft(
                                'additionalExperienceStart',
                                datePickerValueToIsoDate(v)
                              )
                            }
                            TextFieldProps={{
                              ...hcpProfileDatePickerTextFieldProps,
                              error: Boolean(
                                expFieldErrors.additionalExperienceStart
                              ),
                              helperText:
                                expFieldErrors.additionalExperienceStart || '',
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DatePicker
                            label={eduExpFieldLabel(
                              'Additional experience end',
                              'expInternshipEnd'
                            )}
                            fullWidth
                            disabled={expBusy}
                            value={isoDateToDatePickerValue(
                              expDraft.additionalExperienceEnd
                            )}
                            onChange={(v) =>
                              updateExpDraft(
                                'additionalExperienceEnd',
                                datePickerValueToIsoDate(v)
                              )
                            }
                            TextFieldProps={{
                              ...hcpProfileDatePickerTextFieldProps,
                              error: Boolean(
                                expFieldErrors.additionalExperienceEnd
                              ),
                              helperText:
                                expFieldErrors.additionalExperienceEnd || '',
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <div className={profileStyles.attachmentPanel}>
                            <ProfileWizardSingleFileField
                              id="hcp_profile_exp_additional_cert"
                              label={eduExpFieldLabel(
                                'Additional experience certificate',
                                'expAdditionalExperienceCertificate'
                              )}
                              disabled={expBusy}
                              localFile={
                                expDraft.additionalExperienceCertificateFile
                              }
                              existingServerFile={
                                expDraft.existingAdditionalExperienceCertificateUploadId
                                  ? expData.additionalExperienceCertificateObj
                                  : null
                              }
                              onFileChange={(f) => {
                                updateExpDraft(
                                  'additionalExperienceCertificateFile',
                                  f
                                );
                                if (f) {
                                  updateExpDraft(
                                    'existingAdditionalExperienceCertificateUploadId',
                                    null
                                  );
                                }
                              }}
                              onClearExistingServerFile={() =>
                                updateExpDraft(
                                  'existingAdditionalExperienceCertificateUploadId',
                                  null
                                )
                              }
                              preset="attachment"
                              requiredHint
                              showError={Boolean(
                                expFieldErrors.additionalExperienceCertificateFile
                              )}
                              helperText={
                                expFieldErrors.additionalExperienceCertificateFile ||
                                'Required when additional experience is Yes unless keeping current file.'
                              }
                            />
                            {showAttachmentPriorVersions ? (
                              <AttachmentSlotRevisionHistory
                                slotKey="experience:additional_experience_certificate"
                                attachmentRevisions={attachmentRevisions}
                                title="Prior additional-experience uploads"
                              />
                            ) : null}
                          </div>
                        </Grid>
                      </>
                    ) : null}
                  </>
                ) : null}

                <Grid item xs={12} style={{ marginTop: 8 }}>
                  <TextField
                    {...hcpProfileTextFieldProps}
                    label={eduExpFieldLabel(
                      'Undergraduate internship',
                      'expUndergraduateInternship'
                    )}
                    disabled={expBusy}
                    multiline
                    minRows={3}
                    value={expDraft.undergraduateInternship || ''}
                    onChange={(e) =>
                      updateExpDraft('undergraduateInternship', e.target.value)
                    }
                    error={Boolean(expFieldErrors.undergraduateInternship)}
                    helperText={expFieldErrors.undergraduateInternship || ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={eduExpFieldLabel(
                      'Undergraduate internship start',
                      'expUndergraduateInternshipStart'
                    )}
                    fullWidth
                    disabled={expBusy}
                    value={isoDateToDatePickerValue(
                      expDraft.undergraduateInternshipStart
                    )}
                    onChange={(v) =>
                      updateExpDraft(
                        'undergraduateInternshipStart',
                        datePickerValueToIsoDate(v)
                      )
                    }
                    TextFieldProps={{
                      ...hcpProfileDatePickerTextFieldProps,
                      error: Boolean(
                        expFieldErrors.undergraduateInternshipStart
                      ),
                      helperText:
                        expFieldErrors.undergraduateInternshipStart || '',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={eduExpFieldLabel(
                      'Undergraduate internship end',
                      'expUndergraduateInternshipEnd'
                    )}
                    fullWidth
                    disabled={expBusy}
                    value={isoDateToDatePickerValue(
                      expDraft.undergraduateInternshipEnd
                    )}
                    onChange={(v) =>
                      updateExpDraft(
                        'undergraduateInternshipEnd',
                        datePickerValueToIsoDate(v)
                      )
                    }
                    TextFieldProps={{
                      ...hcpProfileDatePickerTextFieldProps,
                      error: Boolean(expFieldErrors.undergraduateInternshipEnd),
                      helperText:
                        expFieldErrors.undergraduateInternshipEnd || '',
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <div className={profileStyles.attachmentPanel}>
                    <ProfileWizardSingleFileField
                      id="hcp_profile_exp_undergrad_cert"
                      label={eduExpFieldLabel(
                        'Undergraduate internship certificate',
                        'expUndergraduateInternshipCertificate'
                      )}
                      disabled={expBusy}
                      localFile={
                        expDraft.undergraduateInternshipCertificateFile
                      }
                      existingServerFile={
                        expDraft.existingUndergraduateInternshipCertificateUploadId
                          ? expData.undergraduateInternshipCertificateObj
                          : null
                      }
                      onFileChange={(f) => {
                        updateExpDraft(
                          'undergraduateInternshipCertificateFile',
                          f
                        );
                        if (f) {
                          updateExpDraft(
                            'existingUndergraduateInternshipCertificateUploadId',
                            null
                          );
                        }
                      }}
                      onClearExistingServerFile={() =>
                        updateExpDraft(
                          'existingUndergraduateInternshipCertificateUploadId',
                          null
                        )
                      }
                      preset="attachment"
                      requiredHint={
                        educationExperienceConfigDefaults.experience
                          .undergraduateInternCertificateRequired
                      }
                      showError={Boolean(
                        expFieldErrors.undergraduateInternshipCertificateFile
                      )}
                      helperText={
                        expFieldErrors.undergraduateInternshipCertificateFile ||
                        (educationExperienceConfigDefaults.experience
                          .undergraduateInternCertificateRequired
                          ? 'Required unless the current file is kept.'
                          : undefined)
                      }
                    />
                    {showAttachmentPriorVersions ? (
                      <AttachmentSlotRevisionHistory
                        slotKey="experience:undergraduate_internship_certificate"
                        attachmentRevisions={attachmentRevisions}
                        title="Prior undergraduate-internship uploads"
                      />
                    ) : null}
                  </div>
                </Grid>

                <Grid item xs={12} style={{ marginTop: 8 }}>
                  <div className={profileStyles.attachmentPanel}>
                    <ProfileWizardMultiFileField
                      id="hcp_profile_exp_relevant_attachments"
                      label="Supporting attachments (PDF/JPG/PNG)"
                      disabled={expBusy}
                      localFiles={expDraft.relevantAttachmentFiles || []}
                      existingServerFiles={(
                        expDraft.existingRelevantAttachmentIds || []
                      )
                        .map((fid) => {
                          const meta = expData.relevantAttachments?.find(
                            (a) => a.id === fid
                          );
                          return (
                            meta || {
                              id: fid,
                              originalName: `Document #${fid}`,
                            }
                          );
                        })
                        .filter(Boolean)}
                      onLocalFilesChange={(next) => {
                        setExpFieldErrors((prev) => {
                          if (!prev.relevantAttachmentFiles) return prev;
                          const o = { ...prev };
                          delete o.relevantAttachmentFiles;
                          return o;
                        });
                        setExpDraft((p) =>
                          p ? { ...p, relevantAttachmentFiles: next } : p
                        );
                      }}
                      onServerFilesRemoved={(ids) => {
                        setExpFieldErrors((prev) => {
                          if (!prev.relevantAttachmentFiles) return prev;
                          const o = { ...prev };
                          delete o.relevantAttachmentFiles;
                          return o;
                        });
                        setExpDraft((p) =>
                          p
                            ? {
                                ...p,
                                existingRelevantAttachmentIds: (
                                  p.existingRelevantAttachmentIds || []
                                ).filter((x) => !ids.includes(x)),
                              }
                            : p
                        );
                      }}
                      maxItems={
                        educationExperienceConfigDefaults.experience
                          .relevantAttachmentsMax
                      }
                      showError={Boolean(
                        expFieldErrors.relevantAttachmentFiles
                      )}
                      helperText={
                        expFieldErrors.relevantAttachmentFiles ||
                        (expDraft.existingRelevantAttachmentIds?.length
                          ? `Keeping ${expDraft.existingRelevantAttachmentIds.length} existing file(s). New files are added in addition to those retained on save.`
                          : undefined)
                      }
                    />
                  </div>
                </Grid>
              </Grid>

              <Box
                display="flex"
                gap={1}
                flexWrap="wrap"
                alignItems="center"
                mt={3}
              >
                <Button
                  variant="secondary"
                  status="danger"
                  size="small"
                  className={buttonStyles.button}
                  onClick={cancelExp}
                  disabled={expBusy}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  className={buttonStyles.button}
                  disabled={expBusy}
                  onClick={saveExp}
                >
                  {expBusy ? (
                    <>
                      <CircularProgress size={18} style={{ marginRight: 8 }} />
                      Saving…
                    </>
                  ) : (
                    'Save professional experience'
                  )}
                </Button>
              </Box>
            </div>
          )
        )}
      </section>
    </div>
  );
}
